/**
 * Whether this document is running inside another page's frame, and what
 * that means for credentials.
 *
 * The platform refuses to be framed (`frame-ancestors 'none'`) except for the
 * public-arche embed URL, `/a/<slug>?embed=true`. That one exception is decided
 * at the edge, which cannot know whether an arche is embeddable, so a framed
 * document may be any page at all under `/a/`. The SPA therefore enforces the
 * rest, and does it once, at the root:
 *
 * - a framed document mounts `EmbedApp` instead of `App` (see `main.tsx`), so
 *   the only thing it can ever render is an embeddable arche — no other route
 *   exists in that tree for a link or a plugin navigation to escape to;
 * - a framed document never sends the visitor's cookies (`sessionCredentials`),
 *   so whatever it renders runs signed out. Clickjacking needs the victim's
 *   session; without cookies there is none to borrow.
 *
 * "Framed" is `window.self !== window.top`. The embedding page cannot make
 * that false: it can only sandbox the frame without scripts, and then nothing
 * renders at all. Evaluated once at load — a document does not move between
 * frames.
 */

/** Pure form of the check, for tests. A cross-origin `top` that throws counts as framed. */
export function isFramedWindow(win: Pick<Window, 'self' | 'top'>): boolean {
  try {
    return win.self !== win.top;
  } catch {
    return true;
  }
}

/** True when this document is inside another page's frame. False during SSR. */
export const IS_EMBED_DOCUMENT: boolean = typeof window !== 'undefined' && isFramedWindow(window);

/**
 * The `credentials` mode for every API `fetch` the web app makes. The ONLY
 * place `'include'` may appear — pinned by `embed-document.test.ts` — so a new
 * call site cannot quietly send the session from a framed document.
 */
export function sessionCredentials(): RequestCredentials {
  return IS_EMBED_DOCUMENT ? 'omit' : 'include';
}
